// ═══════════════════════════════════════════════════════
// LocalStorageAdapter — Phase 1 Persistence
// Implementiert IPersistence via LocalStorage.
// In Phase 3 durch ApiAdapter ersetzt — kein anderer Code ändert sich.
// ═══════════════════════════════════════════════════════
import type {
  IPersistence, PlayerProfile, QuestProgress, MatchResult
} from '@interfaces/index';

const KEYS = {
  PROFILE: 'ultimaze_profile',
  QUEST: 'ultimaze_quest',
  MATCHES: 'ultimaze_matches',
  PACK_METER: 'ultimaze_pack_meter',
} as const;

export class LocalStorageAdapter implements IPersistence {
  async saveProfile(profile: PlayerProfile): Promise<void> {
    try {
      localStorage.setItem(KEYS.PROFILE, JSON.stringify(profile));
    } catch (e) {
      console.error('[LocalStorageAdapter] saveProfile failed:', e);
    }
  }

  async loadProfile(): Promise<PlayerProfile | null> {
    try {
      const raw = localStorage.getItem(KEYS.PROFILE);
      return raw ? (JSON.parse(raw) as PlayerProfile) : null;
    } catch (e) {
      console.error('[LocalStorageAdapter] loadProfile failed:', e);
      return null;
    }
  }

  async saveQuestProgress(progress: QuestProgress): Promise<void> {
    try {
      localStorage.setItem(KEYS.QUEST, JSON.stringify(progress));
    } catch (e) {
      console.error('[LocalStorageAdapter] saveQuestProgress failed:', e);
    }
  }

  async getPackMeter(): Promise<number> {
    try {
      return parseFloat(localStorage.getItem(KEYS.PACK_METER) ?? '0');
    } catch {
      return 0;
    }
  }

  async setPackMeter(value: number): Promise<void> {
    try {
      localStorage.setItem(KEYS.PACK_METER, String(Math.min(100, Math.max(0, value))));
    } catch (e) {
      console.error('[LocalStorageAdapter] setPackMeter failed:', e);
    }
  }

  async saveMatchResult(result: MatchResult): Promise<void> {
    try {
      const existing = await this.getRecentMatches(20);
      const updated = [result, ...existing].slice(0, 20); // Keep last 20
      localStorage.setItem(KEYS.MATCHES, JSON.stringify(updated));
    } catch (e) {
      console.error('[LocalStorageAdapter] saveMatchResult failed:', e);
    }
  }

  async getRecentMatches(limit: number): Promise<MatchResult[]> {
    try {
      const raw = localStorage.getItem(KEYS.MATCHES);
      const all = raw ? (JSON.parse(raw) as MatchResult[]) : [];
      return all.slice(0, limit);
    } catch {
      return [];
    }
  }
}
